import { html, LitElement, nothing, PropertyValues, TemplateResult } from 'lit';
import { property, state } from 'lit/decorators.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';
import { classMap } from 'lit/directives/class-map.js';

import './ix-file-chip.js';
import DataStorageUnit from './data-storage-unit.js';
import FileInfo from './internal/file-info.js';
import FileUploadResult from './internal/file-upload-result.js';
import FileChangeInfo from './internal/file-change-info.js';
import FilesUploadedInfo from './internal/files-uploaded-info.js';
import getTotalBytes from './internal/get-total-bytes.js';
import { processFiles } from './internal/process-files.js';
import sortFilesAscending from './internal/sort-files-ascending.js';
import IxFileUploaderStyles from './styles/ix-file-uploader-styles.js';
import UploaderFile from './internal/uploader-file.js';
import '@digital-realty/ix-dialog/ix-dialog.js';

export class IxFileUploader extends LitElement {
  static readonly styles = [IxFileUploaderStyles];

  /** @nocollapse  */
  static readonly formAssociated = true;

  private readonly internals = (this as HTMLElement).attachInternals();

  dropzoneRef: Ref<HTMLInputElement> = createRef();

  fileRef: Ref<HTMLInputElement> = createRef();

  @property({ type: Boolean }) showTemplateFileLink: boolean = false;

  @property({ type: Boolean }) allowMultipleFiles: boolean = false;

  @property({ type: Boolean }) allowDuplicates: boolean = false;

  @property() headerText: string = '';

  @property() bodyText: string = '';

  @property() hintText: string | undefined = undefined;

  @property({ type: Array }) defaultFiles: Array<UploaderFile> = [];

  @property({ type: Boolean }) allowDeleteDefaultFiles: boolean = false;

  @property({ type: Boolean }) hideBody: boolean = false;

  @property({ type: Array }) extensions: string[] = [];

  @property({ type: Number }) maxFileCount: number = 10;

  @property() maxFileSizeUnit: DataStorageUnit = DataStorageUnit.MB;

  @property({ type: Number }) maxFileSizeValue: number = 10;

  @property() name: string = 'ix-file-uploader';

  @property({ type: Boolean }) error: boolean = false;

  @property({ type: Boolean }) askConfirmationOnDeleteFile: boolean = false;

  @property() deleteConfirmationDialogLabel: string =
    'Are you sure you want to delete your upload file?';

  @state() isDeleteBulkFileDialogOpen: boolean = false;

  @state() pendingFileDeletion: UploaderFile | undefined = undefined;

  @state() files: Array<UploaderFile> = [];

  @state() errors: Array<string> = [];

  private maxFileSizeInBytes: number = 0;

  /**
   * The associated form element with which this element's value will submit.
   */
  get form() {
    return this.internals.form;
  }

  // called when the user drops files on the dropzone
  private onDrop(e: DragEvent): void {
    e.preventDefault();

    if (!e.dataTransfer) {
      return; // no files dropped
    }

    const filesToProcess = Array.from(e.dataTransfer.files);

    this.process(filesToProcess);
  }

  // This is called when the user selects files from the file dialog
  private onFileChange(e: Event): void {
    const fileList = (e.currentTarget as any).files as FileList;

    const filesToProcess = Array.from(fileList);

    this.process(filesToProcess);

    // clear the files from the file input; we are tracking state at the component level, not in the file input
    this.fileRef.value!.files = new DataTransfer().files;
  }

  get allowedMaxFileCount(): number {
    return this.allowMultipleFiles ? this.maxFileCount : 1;
  }

  // common method for processing files which are dropped or selected from file input dialog
  private process(filesToProcess: Array<File>): void {
    const results = processFiles({
      currentFiles: this.files,
      extensions: this.extensions,
      filesToProcess,
      maxFileCount: this.allowedMaxFileCount,
      maxFileSizeInBytes: this.maxFileSizeInBytes,
      allowDuplicates: this.allowDuplicates,
    });

    this.updateState(results);

    this.onFilesUploaded(results);
  }

  private updateState(results: Array<FileUploadResult> = []): void {
    const filesToUpload: Array<UploaderFile> = [];

    results.forEach(result => {
      if (result.uploaded) {
        filesToUpload.push(Object.assign(result.file, { id: result.id }));
      }
    });

    // handle multiple file setting
    if (this.allowMultipleFiles) {
      this.files = [...this.files, ...filesToUpload];
      return;
    }

    // handle single file setting
    if (filesToUpload.length) {
      const file = filesToUpload[0];
      this.files = [file];
    }
  }

  private onFilesUploaded(results: Array<FileUploadResult>): void {
    this.errors = [];

    const filesNotUploaded = results.filter(result => !result.uploaded);

    const event = new CustomEvent<FilesUploadedInfo>('on-files-uploaded', {
      detail: {
        allFiles: this.files,
        filesUploaded: results.filter(result => result.uploaded),
        filesNotUploaded,
      },
    });

    const uniqueErrors = filesNotUploaded
      ?.map(f => f.message)
      .filter((v, i, a) => a.indexOf(v) === i);
    this.errors = [...uniqueErrors];

    this.dispatchEvent(event);
  }

  private onFileClick(e: CustomEvent<FileInfo>): void {
    const event = new CustomEvent<FileChangeInfo>('on-file-clicked', {
      detail: { allFiles: this.files, file: e.detail.file },
    });

    this.dispatchEvent(event);
  }

  private onFileDelete(e: CustomEvent<FileInfo>): void {
    this.pendingFileDeletion = e.detail.file;

    if (this.askConfirmationOnDeleteFile) {
      this.isDeleteBulkFileDialogOpen = true;
      return;
    }

    this.handleDeleteBulkFile();
  }

  handleDeleteBulkFile() {
    this.files = [...this.files].filter(
      file => file.id !== this.pendingFileDeletion?.id
    );

    const event = new CustomEvent<FileChangeInfo>('on-file-removed', {
      detail: { allFiles: this.files, file: this.pendingFileDeletion },
    });

    this.isDeleteBulkFileDialogOpen = false;
    this.dispatchEvent(event);
  }

  public deleteLastFileWithoutConfirmation(): void {
    if (this.files.length === 0) {
      return; // no files to delete
    }

    this.pendingFileDeletion = this.files[this.files.length - 1];

    this.handleDeleteBulkFile();
  }

  private openFileUploadDialog(): void {
    this.fileRef.value?.click();
  }

  protected override firstUpdated(): void {
    this.files = [...this.defaultFiles];

    // connect the dropzone
    const dropzone = this.dropzoneRef.value;
    if (dropzone) {
      dropzone.addEventListener('dragenter', e => e.preventDefault());
      dropzone.addEventListener('dragover', e => e.preventDefault());
      dropzone.addEventListener('drop', this.onDrop.bind(this));
    }

    // connect the file input
    const fileElem = this.fileRef.value;
    if (fileElem) {
      fileElem.addEventListener('change', this.onFileChange.bind(this));
    }

    // calculate the maxBytes based on props
    // Calculate the max file size in bytes to use for checking if files are too large
    const totalBytesForStorageUnit = getTotalBytes(this.maxFileSizeUnit);
    this.maxFileSizeInBytes = this.maxFileSizeValue * totalBytesForStorageUnit;
  }

  protected update(changedProperties: PropertyValues) {
    super.update(changedProperties);

    if (changedProperties.has('defaultFiles')) {
      this.files = [
        ...this.files.filter(
          file => !this.defaultFiles.some(x => x.name === file.name)
        ),
        ...this.defaultFiles,
      ];
    }
  }

  isFileDeletable(file: UploaderFile): boolean {
    if (this.defaultFiles.some(x => x.name === file.name)) {
      return this.allowDeleteDefaultFiles;
    }

    return true;
  }

  private renderFileList(files: Array<UploaderFile> = []): TemplateResult<1> {
    const sortedFiles = sortFilesAscending(files);
    const chips = sortedFiles.map(
      (file?: UploaderFile) => html`<ix-file-chip
        .file=${file}
        .deletable=${this.isFileDeletable(file!)}
        @on-file-click=${this.onFileClick}
        @on-file-delete=${this.onFileDelete}
      ></ix-file-chip>`
    );

    const uploadedFileListClasses = {
      'uploaded-file-list': true,
      'uploaded-file-list--spaced': !this.hideBody,
    };

    return html`
      <ul class="${classMap(uploadedFileListClasses)}">
        ${chips}
        ${this.showTemplateFileLink
          ? html`<li class="ix-file-uploader__template-file">
              <slot name="template-file"></slot>
            </li>`
          : nothing}
      </ul>
      ${this.renderDeleteBulkFileConfirmationDialog()}
    `;
  }

  protected override updated(_: PropertyValues) {
    const formData = new FormData();

    this.files.forEach(file => {
      formData.append(`${this.name}[]`, file);
    });

    this.internals.setFormValue(formData);
  }

  renderDeleteBulkFileConfirmationDialog() {
    if (!this.askConfirmationOnDeleteFile) return nothing;

    return html`<ix-dialog
      id="deleteFileConfirmationDialog"
      ?open=${this.isDeleteBulkFileDialogOpen}
      preventCloseOnScrimClick="true"
      @closed=${() => {
        this.isDeleteBulkFileDialogOpen = false;
      }}
      @close=${() => {
        this.isDeleteBulkFileDialogOpen = false;
      }}
    >
      <form
        slot="content"
        id="delete-file-id"
        method="dialog"
        class="delete-file-confirmation-dialog"
      >
        ${this.deleteConfirmationDialogLabel}
      </form>
      <div slot="actions">
        <ix-button
          appearance="text"
          @click=${() => {
            this.isDeleteBulkFileDialogOpen = false;
          }}
        >
          <span class="button-label">NO</span>
        </ix-button>
        <ix-button
          appearance="text"
          name="submitBtn"
          @click=${() => this.handleDeleteBulkFile()}
        >
          <span class="button-label">YES</span>
        </ix-button>
      </div>
    </ix-dialog>`;
  }

  renderHintText() {
    if (!this.hintText) {
      return nothing;
    }

    return html`<div class="ix-file-uploader__hint-text">
      ${this.hintText}
    </div>`;
  }

  renderErrors() {
    return html` <div class="error-text">
      ${this.errors.map(e => html`<span>* ${e}</span>`)}
    </div>`;
  }

  renderBody() {
    const accept = this.extensions.map(ext => `.${ext}`).join(', ');

    const classes = {
      'ix-file-uploader--hide-body': this.hideBody,
    };

    return html`<div class=${classMap(classes)}>
      <div
        class="dropzone"
        @click=${this.openFileUploadDialog}
        @keyup=${this.openFileUploadDialog}
        ${ref(this.dropzoneRef)}
      >
        <label class="ix-file-uploader__label" for="fileUploader"
          >${this.headerText}</label
        >
        <div class="ix-file-uploader__help-text" id="fileUploaderDesc">
          ${this.bodyText}
        </div>
        <input
          id="fileUploader"
          accept=${accept}
          class="file-input"
          ?multiple=${this.allowMultipleFiles}
          onchange=${(e: Event) => this.onFileChange(e)}
          ${ref(this.fileRef)}
          aria-describedby="fileUploaderDesc"
          type="file"
        />
      </div>
      ${this.renderErrors()} ${this.renderHintText()}
    </div>`;
  }

  protected render(): TemplateResult<1> {
    return html`<div
      class=${classMap({
        'ix-file-uploader': true,
        error: this.errors.length || this.error,
      })}
    >
      ${this.renderBody()}
      ${this.files.length ? this.renderFileList(this.files) : nothing}
    </div>`;
  }
}
